import { Component, DestroyRef, ElementRef, inject, Inject, ViewChild } from '@angular/core';
import { AuthService } from '../../core/auth/auth.service';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, Validators } from '@angular/forms';
import { fbString } from '../../shared/utils/form.utils';
import { LoginService } from './login.service';
import { TypedRoute } from '../../shared/utils/router.utils';
import { ToastService } from '../../shared/services/toast.service';
import { showSpinner } from '../../shared/utils/common.utils';
import { TranslateService } from '@ngx-translate/core';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FeatureFlagService } from '../../core/services/feature-flag.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent {
  destroyRef = inject(DestroyRef);
  @ViewChild('emailInput') emailInput!: ElementRef;

  emailSent: boolean = false;
  email?: string;
  showDemoLogin: boolean = this.featureFlagService.getFeatureFlag('dev_enable_demo_login');
  showOnlyDemoLogin: boolean = this.featureFlagService.getFeatureFlag('dev_enable_demo_login_only');

  constructor(
    @Inject(ActivatedRoute) private route: TypedRoute<{ token: string }>,
    private authService: AuthService,
    private formBuilder: FormBuilder,
    private loginService: LoginService,
    private toastService: ToastService,
    private translateService: TranslateService,
    private featureFlagService: FeatureFlagService,
  ) {
    this.route.data.subscribe((data) => {
      if (data.token) {
        this.authService
          .verifyLoginJWT(data.token)
          .pipe(showSpinner())
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: (data) => {
              this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
                const redirectPath = params.get('redirect');
                if (redirectPath) {
                  this.authService.logIn(data.key, redirectPath);
                } else {
                  this.authService.logIn(data.key);
                }
              });
            },
            error: (error: HttpErrorResponse) => {
              this.authService.showTokenVerifyErrorToast(error);
            },
          });
      }
    });
  }

  logInForm = this.formBuilder.nonNullable.group({
    email: [fbString, [Validators.required, Validators.email]],
  });

  sendLogInEmail() {
    this.logInForm.markAllAsTouched();
    if (this.logInForm.valid) {
      this.email = this.logInForm.value.email!;
      this.loginService
        .sendLoginEmail(this.email)
        .pipe(showSpinner())
        .subscribe({
          next: () => {
            this.emailSent = true;
            this.toastService.add({
              message: this.translateService.instant('LogIn.WeHaveSentAnEmailToast', { email: this.email }),
              type: 'success',
            });
          },
          error: (error: HttpErrorResponse) => {
            if (error.status === 429) {
              this.toastService.add({
                message: error.error.message ?? this.translateService.instant('Common.Errors.UnknownError'),
                type: 'error',
              });
            } else {
              this.toastService.add({
                message: this.translateService.instant('LogIn.CouldNotSendEmail'),
                type: 'error',
              });
            }
          },
        });
    }
  }

  logIn(): void {
    this.authService.logIn('demotoken');
  }
}
